import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { siteInfo, siteTimeline, testimonials, methodSteps } from "@/mocks/archive";

const principles = [
  {
    title: "只碰公开信息",
    desc: "所有材料来自当事人主动公开的账号、平台公告与公开文章。私人联系方式、住址、非公开关系一律不收不存。",
  },
  {
    title: "区分三档强度",
    desc: "每条信息都标注「已核实」「待核实」「存疑」。孤证只做存疑标记，绝不当结论使用。",
  },
  {
    title: "保留原始版本",
    desc: "公告与帖子可能被修改或删除。存档保留完整快照与抓取时间，原文更新后新旧两版同时保留。",
  },
  {
    title: "修订公开可查",
    desc: "收到当事人的合理说明并核对属实后，我们公开修订并保留修订前后两个版本，让改动本身也可追溯。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们｜溯影档案 · 黑料网红考古的内容原则与核对方法",
    description:
      "了解溯影档案的定位与内容原则：为什么我们做黑料网红考古、如何核对旧言论与旧照、如何处理隐私边界与修订机制，以及读者对我们档案整理方式的真实反馈。",
    keywords: "关于溯影档案,黑料网红考古原则,黑料网红黑历史,档案核对方法,内容原则",
    canonical: "https://scadaforweb.cn/about",
  });

  return (
    <Layout>
      <JsonLd
        id="ld-about"
        data={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于溯影档案",
          description: "溯影档案的定位、内容原则、核对方法与修订机制说明。",
          url: "https://scadaforweb.cn/about",
          inLanguage: "zh-CN",
          mainEntity: {
            "@type": "Organization",
            name: siteInfo.brand,
            email: siteInfo.email,
            foundingDate: "2019",
            description:
              "以档案方式整理网络人物公开言行的观察站，聚焦黑料网红早期发言、旧照旧言论与前科记录的回溯。",
            knowsAbout: ["网络文化观察", "媒介素养", "公开信息整理", "内容考据"],
          },
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://scadaforweb.cn/" },
              { "@type": "ListItem", position: 2, name: "关于我们", item: "https://scadaforweb.cn/about" },
            ],
          },
        }}
      />

      <section className="w-full px-4 md:px-8 pt-8 md:pt-10 pb-10 bg-background-100 border-b border-background-300">
        <Breadcrumb items={[{ name: "首页", to: "/" }, { name: "关于我们" }]} />
        <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
          关于 {siteInfo.brand}
        </h1>
        <p className="mt-4 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
          {siteInfo.intro}
        </p>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
        <div className="order-2 lg:order-1">
          <span className="text-xs tracking-[0.22em] text-primary-600">POSITIONING</span>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            我们把自己定位成一份档案，而不是一个审判席
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-loose mb-4">
            网络记忆是被算法不断冲刷的。一条早期的直播发言，几年后可能只剩下一张被裁掉上下文的截图，
            底下的评论却早已把它当成定论。{siteInfo.brand} 想做的，是把这些碎片按时间顺序放回原位。
          </p>
          <p className="text-sm md:text-base text-foreground-700 leading-loose">
            我们不提供榜单，也不给任何人贴标签。你在这里看到的不是结论，而是一条条标注了出处、
            可以自己动手核对的线索。看不看得完，信不信得过，交给读的人。
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4">
            {siteInfo.stats.slice(0, 2).map((stat) => (
              <div key={stat.label} className="p-4 rounded-lg bg-background-100 border border-background-300">
                <p className="font-heading text-2xl font-bold text-primary-600">
                  {stat.value}
                  <span className="ml-1 text-xs font-normal text-foreground-500">{stat.unit}</span>
                </p>
                <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="order-1 lg:order-2 w-full h-64 md:h-96 rounded-lg overflow-hidden border border-background-300">
          <img
            src="https://readdy.ai/api/search-image?query=Overhead%20view%20of%20a%20warm%20cream%20wooden%20archive%20desk%20with%20labeled%20paper%20folders%2C%20a%20brass%20magnifier%2C%20stacked%20old%20photographic%20prints%20and%20a%20cup%20of%20black%20tea%2C%20rust%20red%20thread%20and%20sealing%20wax%2C%20soft%20natural%20window%20light%2C%20muted%20earthy%20palette%2C%20calm%20editorial%20still%20life&width=1100&height=1000&seq=suying-about-desk-01&orientation=portrait"
            alt="溯影档案团队整理公开材料时的档案桌面场景"
            title="黑料网红旧言论整理 - 档案桌面"
            className="w-full h-full object-top"
          />
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mb-8 md:mb-10">
          <span className="text-xs tracking-[0.22em] text-primary-600">PRINCIPLES</span>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            四条不会让步的内容原则
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {principles.map((item, index) => (
            <div
              key={item.title}
              className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-300"
            >
              <span className="font-heading text-sm text-primary-500">0{index + 1}</span>
              <h3 className="mt-2 font-heading text-lg font-semibold text-foreground-950 mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-accent-50">
        <div className="max-w-3xl mb-8 md:mb-10">
          <span className="text-xs tracking-[0.22em] text-accent-700">HOW WE WORK</span>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            一份档案从线索到发布的四个环节
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {methodSteps.map((step) => (
            <div key={step.step} className="p-5 rounded-lg bg-background-50 border border-accent-200">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-800 text-lg">
                  <i className={step.icon}></i>
                </span>
                <span className="font-heading text-sm font-bold text-accent-700">{step.step}</span>
              </div>
              <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                {step.title}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs tracking-[0.22em] text-primary-600">TIMELINE</span>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              一路做下来的样子
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-loose">
              从一个人的收藏夹，到一套公开可核对的档案流程，中间没有捷径，只有一次次把来源重新确认一遍。
            </p>
          </div>
          <ol className="lg:col-span-7 relative pl-6 border-l border-background-300 space-y-6">
            {siteTimeline.map((node) => (
              <li key={node.year} className="relative">
                <span className="absolute -left-[29px] top-1.5 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50"></span>
                <p className="font-heading text-sm font-bold text-primary-600 mb-1">{node.year}</p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{node.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100 border-y border-background-300">
        <div className="max-w-3xl mb-8 md:mb-10">
          <span className="text-xs tracking-[0.22em] text-primary-600">READER NOTES</span>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            读者留下的几句话
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {testimonials.map((item) => (
            <figure key={item.name} className="p-6 rounded-lg bg-background-50 border border-background-300">
              <i className="ri-double-quotes-l text-2xl text-primary-300 mb-3"></i>
              <blockquote className="text-sm text-foreground-700 leading-relaxed">
                {item.text}
              </blockquote>
              <figcaption className="mt-4 pt-3 border-t border-background-300 text-sm">
                <span className="font-medium text-foreground-950">{item.name}</span>
                <span className="ml-2 text-xs text-foreground-500">{item.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-14 md:py-20">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 p-6 md:p-10 rounded-lg bg-background-100 border border-background-300">
          <div className="max-w-2xl">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
              联系方式
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              线索提交、内容修订与转载事宜，都可以通过下面的方式找到我们。
            </p>
            <ul className="space-y-2 text-sm text-foreground-700">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-500"></i>
                <a href={`mailto:${siteInfo.email}`} className="hover:text-primary-600 transition-colors">
                  {siteInfo.email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-timer-line text-primary-500"></i>
                <span>{siteInfo.replyTime}</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-map-pin-line text-primary-500"></i>
                <span>{siteInfo.location}（线上协作）</span>
              </li>
            </ul>
          </div>
          <Link
            to="/contact"
            className="shrink-0 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors text-center whitespace-nowrap"
          >
            前往联系页提交
          </Link>
        </div>
      </section>
    </Layout>
  );
}